/**
 * Utilitários para datas "date-only" (colunas `date` do Postgres/Supabase).
 *
 * POR QUE ESTE ARQUIVO EXISTE:
 * Colunas `date` chegam ao front como string "YYYY-MM-DD".
 * `new Date("2026-09-01")` é interpretado pelo JS como meia-noite **UTC**.
 * Em America/Sao_Paulo (UTC-3) isso vira 31/08 às 21h — ou seja, toda data de
 * dia inteiro é exibida/comparada UM DIA ANTES.
 *
 * REGRA: nunca use `new Date(stringDeData)` nem `toISOString().split("T")[0]`
 * para valores vindos de colunas `date`. Use as funções abaixo.
 *
 * Colunas `timestamptz` (created_at, updated_at, etc.) têm fuso real e podem
 * continuar usando `new Date(valor)` normalmente.
 */
import { format as dfFormat } from "date-fns";
import { ptBR } from "date-fns/locale";

/** Converte "YYYY-MM-DD" (ou ISO completo) em Date à meia-noite LOCAL. */
export function parseDateOnly(value?: string | null): Date | null {
  if (!value) return null;
  const raw = String(value).trim();
  if (!raw) return null;
  const datePart = raw.includes("T") ? raw.split("T")[0] : raw.split(" ")[0];
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(datePart);
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  if (!year || month < 1 || month > 12 || day < 1 || day > 31) return null;
  const d = new Date(year, month - 1, day);
  if (Number.isNaN(d.getTime())) return null;
  return d;
}

/** Formata uma data date-only com date-fns (locale ptBR). "" se inválida. */
export function formatDateOnly(value?: string | null, pattern = "dd/MM/yyyy"): string {
  const d = parseDateOnly(value);
  if (!d) return "";
  return dfFormat(d, pattern, { locale: ptBR });
}

/** Converte um Date LOCAL em "YYYY-MM-DD" sem passar por UTC. */
export function toDateOnly(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/** Data de hoje (local) como "YYYY-MM-DD". */
export function todayDateOnly(): string {
  return toDateOnly(new Date());
}

/** Date à meia-noite local de hoje. */
export function startOfTodayLocal(): Date {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
}

/** true se a data date-only é hoje (comparação de string). */
export function isDateOnlyToday(value?: string | null): boolean {
  if (!value) return false;
  const raw = String(value).trim();
  const datePart = raw.includes("T") ? raw.split("T")[0] : raw.split(" ")[0];
  return datePart === todayDateOnly();
}

/** true se a data date-only é estritamente anterior a hoje (hoje NÃO é atraso). */
export function isDateOnlyPast(value?: string | null): boolean {
  if (!value) return false;
  const raw = String(value).trim();
  const datePart = raw.includes("T") ? raw.split("T")[0] : raw.split(" ")[0];
  if (!/^\d{4}-\d{2}-\d{2}$/.test(datePart)) return false;
  return datePart < todayDateOnly();
}

/** true se a data date-only é estritamente posterior a hoje. */
export function isDateOnlyFuture(value?: string | null): boolean {
  if (!value) return false;
  const raw = String(value).trim();
  const datePart = raw.includes("T") ? raw.split("T")[0] : raw.split(" ")[0];
  if (!/^\d{4}-\d{2}-\d{2}$/.test(datePart)) return false;
  return datePart > todayDateOnly();
}

/** Soma dias a uma data date-only, devolvendo "YYYY-MM-DD". */
export function addDaysDateOnly(value: string, days: number): string {
  const d = parseDateOnly(value);
  if (!d) return value;
  d.setDate(d.getDate() + days);
  return toDateOnly(d);
}

/** Diferença em dias inteiros entre duas datas date-only (b - a). */
export function diffDaysDateOnly(a?: string | null, b?: string | null): number | null {
  const da = parseDateOnly(a);
  const db = parseDateOnly(b);
  if (!da || !db) return null;
  return Math.round((db.getTime() - da.getTime()) / 86400000);
}

/** Dias restantes de hoje até a data date-only informada. */
export function daysUntilDateOnly(value?: string | null): number | null {
  return diffDaysDateOnly(todayDateOnly(), value);
}

/** Dia local (YYYY-MM-DD) em America/Sao_Paulo de um timestamptz. Nunca corta a string no "T". */
export function dateOnlyFromTimestampSP(ts?: string | null): string | null {
  if (!ts) return null;
  const ms = Date.parse(ts);
  if (Number.isNaN(ms)) return null;
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Sao_Paulo", year: "numeric", month: "2-digit", day: "2-digit",
  }).format(ms);
}
